import { lazy, Suspense, useEffect } from "react";
import { Switch, Route, Redirect, Router as WouterRouter, useSearch, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MotionConfig } from "framer-motion";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { setCsrfToken, useGetMe, getGetMeQueryKey } from "@workspace/api-client-react";
import NotFound from "@/pages/not-found";
import Home from "@/pages/home";
import ConfirmationPage from "@/pages/confirmation";
import JobPostedPage from "@/pages/job-success";
import UnsubscribeSuccessPage from "@/pages/unsubscribe-success";
import UnsubscribeConfirmPage from "@/pages/unsubscribe-confirm";
import UnsubscribedPage from "@/pages/unsubscribed";
import LoginPage from "@/pages/login";
import Layout from "@/components/layout";
import OgTemplate from "@/pages/og-template";
import { useTrackEvent } from "@/hooks/use-track-event";
import { initPostHog } from "@/lib/posthog";
import ErrorBoundary from "@/components/error-boundary";

const ProfilePage = lazy(() => import("@/pages/profile"));
const OnboardingPage = lazy(() => import("@/pages/onboarding"));
const ProfileEditPage = lazy(() => import("@/pages/profile-edit"));
const AdminPage = lazy(() => import("@/pages/admin"));
const JobNewPage = lazy(() => import("@/pages/job-new"));
const JobDetailPage = lazy(() => import("@/pages/job-detail"));
const ManageJobPage = lazy(() => import("@/pages/manage-job"));
const ResendManagementLinkPage = lazy(() => import("@/pages/resend-management-link"));
const DashboardPage = lazy(() => import("@/pages/dashboard"));
const RecruiterDashboardPage = lazy(() => import("@/pages/recruiter-dashboard"));
const MyJobsPage = lazy(() => import("@/pages/my-jobs"));
const CommunitiesEventsPage = lazy(() => import("@/pages/communities-events"));
const CommunitiesEventsNewPage = lazy(() => import("@/pages/communities-events-new"));
const CommunitiesEventsEditPage = lazy(() => import("@/pages/communities-events-edit"));
const FeedbackPage = lazy(() => import("@/pages/feedback"));
const PlatformPage = lazy(() => import("@/pages/platform"));
const AboutPage = lazy(() => import("@/pages/about"));
const RecruiterSavedPage = lazy(() => import("@/pages/recruiter-saved"));
const AlumniSavedPage = lazy(() => import("@/pages/alumni-saved"));
const JoinPage = lazy(() => import("@/pages/join"));

const queryClient = new QueryClient();

const LazyFallback = (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

function PageViewTracker() {
  const [path] = useLocation();
  const track = useTrackEvent();

  useEffect(() => {
    track("page_view", { path });
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function CsrfTokenLoader() {
  const { data: me } = useGetMe({
    query: { queryKey: getGetMeQueryKey(), retry: false },
  });

  const userId = me?.id ?? null;

  useEffect(() => {
    fetch("/api/csrf-token", { credentials: "include" })
      .then((res) => res.json())
      .then((data: { token?: string }) => {
        setCsrfToken(data.token ?? null);
      })
      .catch(() => {});
  }, [userId]);

  return null;
}

function LoginRoute() {
  const searchString = useSearch();
  const params = new URLSearchParams(searchString);
  const returnTo = params.get("returnTo") ?? "/";

  const { data: me, isLoading: meLoading } = useGetMe({
    query: { queryKey: getGetMeQueryKey(), retry: false },
  });

  if (meLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (me) {
    return <Redirect to={returnTo} />;
  }

  return <LoginPage />;
}

function Router() {
  return (
    <Switch>
      <Route path="/design/og" component={OgTemplate} />
      <Route>
        <Layout>
          {/*
           * ROUTE ORDERING RULE — static before parameterized
           *
           * wouter matches top-to-bottom within a Switch. A static path that
           * shares a prefix with a parameterised sibling MUST appear first,
           * otherwise the param route swallows it. Examples:
           *
           *   /profile/edit     must precede  /profile/:id
           *   /jobs/new         must precede  /jobs/:id
           *   /jobs/posted      must precede  /jobs/:id
           *   /jobs/manage/…    must precede  /jobs/:id
           *   /communities-events/new      must precede  /communities-events/:id/edit
           *
           * When adding a new static sub-path, place it ABOVE every Route for
           * the same prefix that contains a URL parameter.
           */}
          <Suspense fallback={LazyFallback}>
            <Switch>
              <Route path="/" component={Home} />
              <Route path="/join/:code" component={JoinPage} />
              <Route path="/onboarding" component={OnboardingPage} />
              <Route path="/confirmation" component={ConfirmationPage} />
              {/* Static /profile/* routes before parameterised /profile/:id */}
              <Route path="/profile/edit" component={ProfileEditPage} />
              <Route path="/profile/:id" component={ProfilePage} />
              <Route path="/recruiter/register"><Redirect to="/recruiter/dashboard" /></Route>
              <Route path="/recruiter/login"><Redirect to="/recruiter/dashboard" /></Route>
              <Route path="/admin" component={AdminPage} />
              {/* Static /jobs/* routes before parameterised /jobs/:id */}
              <Route path="/jobs/new" component={JobNewPage} />
              <Route path="/jobs/posted" component={JobPostedPage} />
              <Route path="/jobs/manage/resend" component={ResendManagementLinkPage} />
              <Route path="/jobs/manage" component={ManageJobPage} />
              <Route path="/jobs/:id" component={JobDetailPage} />
              <Route path="/about" component={AboutPage} />
              <Route path="/my-jobs" component={MyJobsPage} />
              <Route path="/dashboard" component={DashboardPage} />
              <Route path="/unsubscribe-confirm" component={UnsubscribeConfirmPage} />
              <Route path="/unsubscribe-success" component={UnsubscribeSuccessPage} />
              <Route path="/unsubscribed" component={UnsubscribedPage} />
              <Route path="/saved" component={AlumniSavedPage} />
              <Route path="/recruiter/saved" component={RecruiterSavedPage} />
              <Route path="/recruiter/dashboard" component={RecruiterDashboardPage} />
              <Route path="/platform" component={PlatformPage} />
              <Route path="/feedback"><Redirect to="/platform?tab=feedback" /></Route>
              <Route path="/features"><Redirect to="/platform?tab=features" /></Route>
              {/* Static /communities-events/* routes before parameterised /communities-events/:id/edit */}
              <Route path="/communities-events/new" component={CommunitiesEventsNewPage} />
              <Route path="/communities-events/:id/edit" component={CommunitiesEventsEditPage} />
              <Route path="/communities-events" component={CommunitiesEventsPage} />
              <Route path="/login" component={LoginRoute} />
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </Layout>
      </Route>
    </Switch>
  );
}

function App() {
  useEffect(() => {
    initPostHog();
  }, []);

  return (
    <ErrorBoundary>
      <MotionConfig reducedMotion="user">
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <CsrfTokenLoader />
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
              <PageViewTracker />
              <Router />
            </WouterRouter>
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </MotionConfig>
    </ErrorBoundary>
  );
}

export default App;
